/*
 OK FBI — Case Deep Analytics ("تقرير القضية")
 Host-only. Post-case FBI-style investigation report with a dedicated "وضع العرض"
 cinematic full-screen view suitable for showing on the livestream.

 Spoiler guarantee: culprit-related stats (vote accuracy, top vote correctness,
 evidence influence) are ONLY shown after the host officially reveals the case
 result (backend enforces this too).
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { X, Presentation, RefreshCcw, FileBarChart2, Users, Fingerprint, Camera, Clock, CheckCircle2, XCircle, Radio } from "lucide-react";

const secClock = (s) => {
  s = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return h ? `${h}:${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}` : `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
};

export function CaseAnalyticsButton({ caseId, label = "تقرير القضية" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`open-analytics-${caseId}`}
        className="text-[11px] font-cairo px-3 py-1.5 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1.5">
        <FileBarChart2 size={12} /> {label}
      </button>
      <CaseAnalyticsModal open={open} onClose={() => setOpen(false)} caseId={caseId} />
    </>
  );
}

export function CaseAnalyticsModal({ open, onClose, caseId }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("summary");
  const [presentation, setPresentation] = useState(false);
  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try { const { data } = await api.get(`/session/analytics/${caseId}`); setReport(data); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;
  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl" data-testid="analytics-modal"
        className="fixed inset-0 z-[999] bg-black/95 text-white p-4 md:p-6 flex flex-col overflow-auto"
        style={{
          backgroundImage: "linear-gradient(135deg, rgba(197,160,89,0.04) 0%, transparent 50%)"
        }}>
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <FileBarChart2 size={22} className="text-gold" strokeWidth={1.2} />
            <div>
              <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">CASE REPORT · OK FBI</div>
              <div className="font-cairo text-2xl">تقرير القضية</div>
            </div>
            {report?.case && (
              <div className="border border-metal/60 px-2 py-1 mr-3">
                <div className="text-[9px] font-mono-ar text-gold">CASE {report.case.code}</div>
                <div className="text-[12px] font-cairo text-white truncate max-w-xs">{report.case.title}</div>
              </div>
            )}
            <ClassificationBadge report={report} />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="analytics-refresh"
              className="text-[11px] font-cairo px-2 py-1.5 border border-metal hover:border-gold hover:text-gold text-zinc-300 flex items-center gap-1"><RefreshCcw size={11} /> تحديث</button>
            <button onClick={() => setPresentation(true)} data-testid="analytics-presentation"
              className="text-[11px] font-cairo px-2 py-1.5 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1"><Presentation size={11} /> وضع العرض</button>
            <button onClick={onClose} data-testid="analytics-close" className="w-10 h-10 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={18} /></button>
          </div>
        </div>

        {/* TABS */}
        <div className="flex items-center gap-1 mb-4 border-b border-metal shrink-0" data-testid="analytics-tabs">
          {[
            { k: "summary",     l: "ملخص القضية" },
            { k: "audience",    l: "إحصائيات الجمهور" },
            { k: "evidence",    l: "إحصائيات الأدلة" },
            { k: "suspects",    l: "إحصائيات المتهمين" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`analytics-tab-${t.k}`}
              className={`text-[12px] font-cairo px-4 py-2 border-b-2 -mb-px transition-colors ${tab === t.k ? "border-gold text-gold" : "border-transparent text-zinc-400 hover:text-white"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0">
          {loading && <div className="text-sm text-zinc-500 text-center pt-10">جارٍ تحميل التقرير…</div>}
          {!loading && !report && <div className="text-sm text-red-400 text-center pt-10">تعذّر تحميل التقرير.</div>}
          {report && !presentation && (
            <>
              {tab === "summary"     && <SummaryTab   report={report} />}
              {tab === "audience"    && <AudienceTab  report={report} />}
              {tab === "evidence"    && <EvidenceTab  report={report} />}
              {tab === "suspects"    && <SuspectsTab  report={report} />}
            </>
          )}
        </div>

        {/* PRESENTATION MODE */}
        {presentation && report && (
          <PresentationView report={report} onClose={() => setPresentation(false)} />
        )}

        <div className="text-center pt-4 text-[9px] text-zinc-600 tracking-[0.3em] font-cairo">OK FBI · CASE ANALYTICS · BY OK</div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function ClassificationBadge({ report }) {
  const cls = report?.published ? "CLOSED" : "OPEN";
  const color = report?.published
    ? (report?.status === "solved" ? "text-emerald-400 border-emerald-800" : "text-red-400 border-red-800")
    : "text-amber-400 border-amber-800";
  return (
    <div className={`text-[9px] font-mono-ar tracking-[0.35em] border px-2 py-1 ${color}`} data-testid="analytics-classification">
      {cls}{report?.published && report?.status ? ` · ${report.status.toUpperCase()}` : ""}
    </div>
  );
}

// ============ Tab: Summary ============
function SummaryTab({ report }) {
  const r = report;
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="analytics-summary">
      <StatCard testid="stat-duration" label="مدة التحقيق الكاملة" value={secClock(r.duration.case_seconds)} icon={<Clock size={13} />} />
      <StatCard testid="stat-interr" label="وقت الاستجواب" value={secClock(r.interrogation.total_seconds)} icon={<Radio size={13} />} />
      <StatCard testid="stat-interr-avg" label="متوسط الاستجواب لكل متهم" value={secClock(r.interrogation.avg_seconds_per_suspect)} />
      <StatCard testid="stat-evidence" label="أدلة مكشوفة" value={`${r.evidence.revealed} / ${r.evidence.total}`} />
      <StatCard testid="stat-fp" label="فحوصات البصمات" value={r.usage.fingerprints_run} icon={<Fingerprint size={13} />} />
      <StatCard testid="stat-dna" label="فحوصات DNA" value={r.usage.dna_run} />
      <StatCard testid="stat-cctv" label="فتح كاميرات المراقبة" value={r.usage.cctv_opens} icon={<Camera size={13} />} />
      <StatCard testid="stat-hints" label="التلميحات" value={r.usage.hints_used} />
      <StatCard testid="stat-audio" label="إعادة تشغيل الأدلة الصوتية" value={r.usage.audio_replays} />
      <StatCard testid="stat-participants" label="عدد المشاركين" value={r.audience.participants} icon={<Users size={13} />} />
      <StatCard testid="stat-voters" label="عدد المصوتين" value={r.audience.voters} />
      <StatCard testid="stat-changed" label="مرات تغيير التصويت" value={r.audience.changed_votes} />
      <StatCard testid="stat-difficulty" label="مستوى الصعوبة" value={r.difficulty_label} />
      <StatCard testid="stat-result" label="النتيجة النهائية"
        value={r.published ? (r.status === "solved" ? "✓ محلولة" : "✕ لم تُحل") : "قيد التحقيق"}
        color={r.published ? (r.status === "solved" ? "text-emerald-400" : "text-red-400") : "text-amber-400"} />
      {r.duration.solved_seconds != null && (
        <StatCard testid="stat-solved-time" label="زمن حل القضية" value={secClock(r.duration.solved_seconds)} />
      )}
      {r.audience.vote_accuracy_pct != null && (
        <StatCard testid="stat-accuracy" label="نسبة اختيار المجرم الصحيح" value={`${r.audience.vote_accuracy_pct}%`} color="text-gold" />
      )}
    </div>
  );
}

// ============ Tab: Audience ============
function AudienceTab({ report }) {
  const r = report;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="analytics-audience">
      <div className="panel border-metal p-4">
        <div className="label-caps mb-3">توزيع الأصوات على المشتبه بهم</div>
        <div className="space-y-1.5">
          {(r.audience.counts_by_suspect || []).map(row => {
            const total = r.audience.total_votes || 0;
            const pct = total ? Math.round((row.count / total) * 100) : 0;
            return (
              <div key={row.id} data-testid={`audience-row-${row.id}`}>
                <div className="flex justify-between text-[12px]">
                  <span className="text-white">{row.name}</span>
                  <span className="text-gold font-mono-ar">{row.count} · {pct}%</span>
                </div>
                <div className="h-1.5 bg-black/50 border border-metal/40 relative overflow-hidden">
                  <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {(!r.audience.counts_by_suspect || !r.audience.counts_by_suspect.length) && (
            <div className="text-sm text-zinc-500">لا توجد أصوات بعد.</div>
          )}
        </div>
      </div>
      <div className="panel border-metal p-4">
        <div className="label-caps mb-3">ملخص التصويت</div>
        <StatRow k="عدد المشاركين" v={r.audience.participants} />
        <StatRow k="عدد المصوتين" v={r.audience.voters} />
        <StatRow k="إجمالي الأصوات" v={r.audience.total_votes} />
        <StatRow k="مرات تغيير التصويت" v={r.audience.changed_votes} />
        <StatRow k="أكثر متهم أصوات" v={r.audience.top_voted ? `${r.audience.top_voted.name} (${r.audience.top_voted.count})` : "—"} />
        {r.audience.vote_accuracy_pct != null
          ? <StatRow k="نسبة اختيار المجرم الصحيح" v={`${r.audience.vote_accuracy_pct}%`} highlight />
          : <StatRow k="نسبة اختيار المجرم الصحيح" v="تظهر بعد إعلان النتيجة" muted />}
      </div>
    </div>
  );
}

// ============ Tab: Evidence ============
function EvidenceTab({ report }) {
  const r = report;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="analytics-evidence">
      <div className="panel border-metal p-4">
        <div className="label-caps mb-3">ترتيب كشف الأدلة</div>
        {r.evidence.reveal_order.length === 0 && <div className="text-sm text-zinc-500">لم يُكشف أي دليل بعد.</div>}
        <ol className="space-y-1.5">
          {r.evidence.reveal_order.map((e, i) => (
            <li key={e.id + i} className="flex items-center gap-2 text-[12px]" data-testid={`evidence-order-${i}`}>
              <span className="text-gold font-mono-ar text-[10px] w-6">#{i + 1}</span>
              <span className="text-white truncate flex-1">{e.name}</span>
              {e.importance && <span className="text-[9px] text-zinc-500 font-mono-ar border border-metal/50 px-1">{e.importance}</span>}
            </li>
          ))}
        </ol>
      </div>
      <div className="panel border-metal p-4">
        <div className="label-caps mb-3">الأدلة الأكثر تأثيراً على التصويت</div>
        {!r.published && <div className="text-sm text-zinc-500">تظهر بعد إعلان النتيجة النهائية.</div>}
        {r.published && r.evidence.influence.length === 0 && <div className="text-sm text-zinc-500">لم نرصد تأثيراً واضحاً.</div>}
        <ol className="space-y-1.5">
          {r.evidence.influence.map((e, i) => (
            <li key={e.id} className="flex items-center gap-2 text-[12px]" data-testid={`evidence-influence-${i}`}>
              <span className="text-gold font-mono-ar text-[10px] w-6">#{i + 1}</span>
              <span className="text-white truncate flex-1">{e.name}</span>
              <span className="text-[10px] text-emerald-400 font-mono-ar">{e.vote_changes} تغيير</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 label-caps">تقرير المختبر</div>
        <div className="space-y-1 mt-2">
          {(r.forensic || []).map(f => (
            <div key={f.tool} className="flex items-center justify-between text-[11px] border border-metal/40 p-1.5" data-testid={`forensic-tool-${f.tool}`}>
              <span className="text-white font-cairo">{
                ({ fingerprint: "البصمات", dna: "DNA", audio: "الصوت", documents: "الوثائق", phone: "الهاتف", materials: "المواد" }[f.tool] || f.tool)
              }</span>
              <span className="text-[10px] font-mono-ar text-gold">{f.sample_id || "—"}</span>
              {r.published && f.percentage != null
                ? <span className="text-[10px] text-emerald-400 font-mono-ar">{f.percentage}%</span>
                : <span className="text-[10px] text-zinc-500 font-mono-ar">مكتمل</span>}
            </div>
          ))}
          {!(r.forensic && r.forensic.length) && <div className="text-sm text-zinc-500">لم تُجرَ أي فحوصات.</div>}
        </div>
      </div>
    </div>
  );
}

// ============ Tab: Suspects ============
function SuspectsTab({ report }) {
  const r = report;
  return (
    <div className="panel border-metal p-4" data-testid="analytics-suspects">
      <div className="label-caps mb-3">وقت الاستجواب لكل متهم</div>
      <div className="space-y-2">
        {r.interrogation.by_suspect.map(s => {
          const max = Math.max(1, ...r.interrogation.by_suspect.map(x => x.seconds));
          const pct = Math.round((s.seconds / max) * 100);
          return (
            <div key={s.id} data-testid={`suspect-time-${s.id}`}>
              <div className="flex justify-between text-[12px]">
                <span className="text-white flex items-center gap-2"><span>{s.name}</span><span className="text-[9px] text-zinc-500 font-mono-ar">أسئلة: {s.questions_revealed}</span></span>
                <span className="text-gold font-mono-ar">{secClock(s.seconds)}</span>
              </div>
              <div className="h-1.5 bg-black/50 border border-metal/40 relative overflow-hidden">
                <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
        {(!r.interrogation.by_suspect || !r.interrogation.by_suspect.length) && <div className="text-sm text-zinc-500">لا توجد بيانات استجواب.</div>}
      </div>
    </div>
  );
}

// ============ Presentation Mode ============
function PresentationView({ report, onClose }) {
  return (
    <div className="fixed inset-0 z-[1000] bg-black flex items-center justify-center" data-testid="analytics-presentation-view">
      <style>{`
        @keyframes ana-halo { 0%,100%{opacity:.3;} 50%{opacity:.55;} }
        .ana-halo { animation: ana-halo 6s ease-in-out infinite; }
      `}</style>
      {/* halo */}
      <div className="ana-halo absolute" style={{ width: 900, height: 900, borderRadius: "50%",
           background: "radial-gradient(circle, rgba(197,160,89,0.12) 0%, rgba(197,160,89,0.02) 45%, transparent 70%)", filter: "blur(30px)" }} />
      <div className="relative w-[1180px] max-w-[95vw] max-h-[92vh] overflow-auto p-8 font-cairo" style={{
        border: "1px solid rgba(197,160,89,0.45)",
        background: "linear-gradient(180deg, rgba(15,15,17,0.98), rgba(6,6,8,0.98))",
        boxShadow: "0 20px 60px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(197,160,89,0.06)"
      }}>
        {/* Report header — FBI-style */}
        <div className="flex items-start justify-between border-b border-gold/40 pb-3 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.5em] text-gold font-mono-ar">CLASSIFIED · CASE REPORT</div>
            <div className="text-3xl mt-1 text-white">{report.case.title}</div>
            <div className="text-[11px] text-zinc-400 mt-1">{report.case.location}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] font-mono-ar text-gold tracking-[0.35em]">FILE</div>
            <div className="text-lg text-white font-mono-ar">{report.case.code}</div>
            <div className="text-[9px] font-mono-ar text-zinc-500 mt-1">GEN {new Date(report.generated_at).toLocaleString("ar-EG")}</div>
            <ClassificationBadge report={report} />
          </div>
        </div>

        {/* Grid — top row */}
        <div className="grid grid-cols-4 gap-3 mb-3">
          <BigStat label="مدة التحقيق" value={secClock(report.duration.case_seconds)} />
          <BigStat label="عدد المشاركين" value={report.audience.participants} />
          <BigStat label="عدد المصوتين" value={report.audience.voters} />
          <BigStat label="مستوى الصعوبة" value={report.difficulty_label} />
        </div>
        <div className="grid grid-cols-4 gap-3 mb-3">
          <BigStat label="أدلة مكشوفة" value={`${report.evidence.revealed} / ${report.evidence.total}`} />
          <BigStat label="فحوصات البصمات + DNA" value={report.usage.fingerprints_run + report.usage.dna_run} />
          <BigStat label="فتح كاميرات المراقبة" value={report.usage.cctv_opens} />
          <BigStat label="متوسط الاستجواب" value={secClock(report.interrogation.avg_seconds_per_suspect)} />
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-2 gap-4">
          {/* Vote distribution */}
          <div className="border border-metal/60 p-4">
            <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-2">توزيع الأصوات</div>
            <div className="space-y-2">
              {(report.audience.counts_by_suspect || []).map(row => {
                const total = report.audience.total_votes || 0;
                const pct = total ? Math.round((row.count / total) * 100) : 0;
                return (
                  <div key={row.id}>
                    <div className="flex justify-between text-[12px]">
                      <span className="text-white">{row.name}</span>
                      <span className="text-gold font-mono-ar">{row.count} · {pct}%</span>
                    </div>
                    <div className="h-1.5 bg-black/50 border border-metal/40 relative overflow-hidden">
                      <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-yellow-700 to-yellow-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Interrogation */}
          <div className="border border-metal/60 p-4">
            <div className="text-[10px] tracking-[0.35em] font-mono-ar text-gold mb-2">أوقات الاستجواب</div>
            <div className="space-y-2">
              {report.interrogation.by_suspect.map(s => {
                const max = Math.max(1, ...report.interrogation.by_suspect.map(x => x.seconds));
                const pct = Math.round((s.seconds / max) * 100);
                return (
                  <div key={s.id}>
                    <div className="flex justify-between text-[12px]">
                      <span className="text-white">{s.name}</span>
                      <span className="text-gold font-mono-ar">{secClock(s.seconds)}</span>
                    </div>
                    <div className="h-1.5 bg-black/50 border border-metal/40 relative overflow-hidden">
                      <div className="absolute inset-y-0 right-0 bg-gradient-to-l from-emerald-700 to-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Result banner */}
        {report.published && (
          <div className="mt-4 border-2 p-4 flex items-center gap-3" style={{ borderColor: report.status === "solved" ? "#059669" : "#dc2626" }}>
            {report.status === "solved" ? <CheckCircle2 size={28} className="text-emerald-400" /> : <XCircle size={28} className="text-red-400" />}
            <div className="flex-1">
              <div className="text-[10px] font-mono-ar tracking-[0.35em] text-zinc-400">FINAL RESULT</div>
              <div className={`text-2xl ${report.status === "solved" ? "text-emerald-300" : "text-red-300"}`}>
                {report.status === "solved" ? "تم حل القضية" : "لم تُحل القضية"}
              </div>
            </div>
            {report.audience.vote_accuracy_pct != null && (
              <div className="text-right">
                <div className="text-[9px] font-mono-ar text-zinc-400">نسبة اختيار المجرم الصحيح</div>
                <div className="text-3xl text-gold font-mono-ar">{report.audience.vote_accuracy_pct}%</div>
              </div>
            )}
          </div>
        )}

        <div className="text-center text-[10px] tracking-[0.4em] font-mono-ar text-zinc-500 mt-4">OK FBI · INTERNAL BRIEFING</div>

        <button onClick={onClose} data-testid="analytics-presentation-close"
          className="absolute top-2 left-2 w-9 h-9 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center bg-black/60">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// ============ Small helpers ============
function StatCard({ label, value, icon, testid, color }) {
  return (
    <div className="border border-metal/60 p-3 bg-black/40" data-testid={testid}>
      <div className="text-[9px] tracking-[0.3em] font-mono-ar text-zinc-500 flex items-center gap-1">
        {icon}<span>{label}</span>
      </div>
      <div className={`mt-1 text-2xl font-mono-ar tabular-nums ${color || "text-white"}`}>{value}</div>
    </div>
  );
}
function StatRow({ k, v, highlight, muted }) {
  return (
    <div className="flex justify-between border-b border-metal/40 py-1 text-[12px]">
      <span className="text-zinc-400">{k}</span>
      <span className={`font-mono-ar ${muted ? "text-zinc-500" : highlight ? "text-gold" : "text-white"}`}>{v}</span>
    </div>
  );
}
function BigStat({ label, value }) {
  return (
    <div className="border border-metal/60 bg-black/40 p-3">
      <div className="text-[9px] tracking-[0.35em] font-mono-ar text-zinc-500">{label}</div>
      <div className="mt-1 text-2xl font-mono-ar tabular-nums text-gold">{value}</div>
    </div>
  );
}
